import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ChevronRight, MapPin, PhoneCall, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../mock";
import { ValuationShell, Field, SEL } from "../components/valuation/ValuationShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { n: "01", title: "Tell us about your property", text: "Use the form or call us. We\u2019ll confirm a convenient time \u2014 usually within 48 hours." },
  { n: "02", title: "A senior valuation, in person", text: "Richard or George visits personally \u2014 assessing condition, presentation and current tenant demand in your postcode." },
  { n: "03", title: "A clear, written figure", text: "An evidence-based rental value backed by comparable lets nearby, with an honest pricing strategy." },
  { n: "04", title: "A launch plan, if you instruct us", text: "Professional photography, digital enhancement and a portal launch timed to capture demand from day one." },
];

export default function ValuationInPerson() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", postcode: "", property_type: "", bedrooms: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address) {
      toast({ title: "A few details still need attention", description: "Please fill in your name, email, phone and property address.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/valuations`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <ValuationShell type="in-person" title="In-Person Rental Valuation | New Space Lettings, Hackney" description="Book a complimentary in-person rental valuation with Richard or George — a written figure, comparable evidence and a launch plan for your property.">
      <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">What to expect</p>
          <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">Begin the <span className="text-[#ec008c]">conversation</span></h2>
          <span className="mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
          <div className="mt-8 space-y-6">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-5" data-testid={`valuation-step-${s.n}`}>
                <p className="font-display text-3xl font-700 text-[#d8b074]/70">{s.n}</p>
                <div>
                  <h3 className="font-display text-base font-600 uppercase tracking-wide text-[#16323f]">{s.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#5b5450]">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 space-y-4 border-t border-[#e5ddd0] pt-8">
            <div className="flex items-center gap-4" data-testid="valuation-office"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><MapPin size={18} /></span><p className="text-sm text-[#16323f]">{CONTACT.address}</p></div>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4" data-testid="valuation-phone"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><PhoneCall size={18} /></span><p className="text-sm text-[#16323f] hover:text-[#ec008c]">{CONTACT.phone}</p></a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4" data-testid="valuation-email"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><Mail size={18} /></span><p className="text-sm text-[#16323f] hover:text-[#ec008c]">{CONTACT.email}</p></a>
          </div>
        </div>

        <div className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-lg lg:p-9">
          {done ? (
            <div className="py-6 text-center" data-testid="valuation-success">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white"><Check size={34} /></div>
              <h3 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Thank you!</h3>
              <p className="mx-auto mt-3 max-w-sm text-sm text-[#5b5450]">Your valuation request has been received. Richard, George and the New Space team will be in touch shortly to arrange a visit.</p>
              <button onClick={() => navigate("/")} data-testid="valuation-home-button" className="mt-7 rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">Back to homepage</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="valuation-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" required><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" data-testid="val-name" /></Field>
                <Field label="Phone" required><Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07123 456789" data-testid="val-phone" /></Field>
              </div>
              <Field label="Email" required><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="val-email" /></Field>
              <div className="grid gap-5 sm:grid-cols-3">
                <Field label="Property address" required className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="1 Kingsland Road" data-testid="val-address" /></Field>
                <Field label="Postcode"><Input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} placeholder="E8 4DG" data-testid="val-postcode" /></Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Property type">
                  <select className={SEL} value={form.property_type} onChange={(e) => set("property_type", e.target.value)} data-testid="val-type">
                    <option value="">Select type</option>
                    {["Flat", "House", "House Terraced", "Studio", "Commercial"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Bedrooms">
                  <select className={SEL} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="val-bedrooms">
                    <option value="">Select</option><option value="Studio">Studio</option>
                    {[1, 2, 3, 4, 5, 6].map((b) => <option key={b} value={String(b)}>{b}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Message"><Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything else we should know?" data-testid="val-message" /></Field>
              <button type="submit" disabled={loading} data-testid="val-submit" className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending&hellip;</>) : (<>Book my valuation <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>)}
              </button>
              <p className="text-center text-xs text-[#7a716a]">Free &amp; no-obligation. We&rsquo;ll never share your details.</p>
            </form>
          )}
        </div>
      </div>
    </ValuationShell>
  );
}
